import { Injectable } from '@angular/core';
import { _Service } from './_service';
import { AppId, AttributeDefinition, AttributeDefinitionTree } from '../../model';

/**
 * A ProductAttributeInstance blocking an attribute-definition delete, resolved to its source.
 * References are subtree-scoped, so `definitionId`/`definitionName` may name a DESCENDANT of the
 * definition that was queried — that descendant is the material actually blocking the delete.
 */
export interface DefinitionReference {
  instanceId: AppId;
  definitionId: AppId;
  definitionName: string;
  elementId?: AppId;
  elementName?: string;
  productId?: AppId;
  productName?: string;
  companyId?: AppId;
  siteId?: AppId;
}

export interface HarmonizeTypeResult {
  changed: number;
  /** Already of the target type — counted, not an error. */
  skipped: number;
}

@Injectable({ providedIn: 'root' })
export class AttributeDefinitionService extends _Service<AttributeDefinition> {
  constructor() {
    super('api/admin/attribute-definition');
  }

  tree = async () => this.http.get<AttributeDefinitionTree[]>('api/attribute-definition');
  listAll = async () => this.http.get<AttributeDefinition[]>(this.prefix);

  createProperty = async (defId: AppId, data: any) =>
    this.http.post<AttributeDefinition>(`${this.prefix}/${defId}/property`, data);

  updateProperty = async (defId: AppId, propId: AppId, data: any) =>
    this.http.put<AttributeDefinition>(`${this.prefix}/${defId}/property/${propId}`, data);

  deleteProperty = async (defId: AppId, propId: AppId) =>
    this.http.delete<AttributeDefinition>(`${this.prefix}/${defId}/property/${propId}`);

  /**
   * Set one propertyKey's type across several definitions at once — the fix for a type clash
   * surfaced by the legal-norm requirement editor. Rejected (409) when a stored attribute value
   * would not survive the conversion.
   */
  harmonizePropertyType = async (data: { propertyKey: string; type: string; definitionIds: AppId[] }) =>
    this.http.post<HarmonizeTypeResult>(`${this.prefix}/property/harmonize-type`, data);

  deleteById = async (id: AppId) => this.http.delete<void>(`${this.prefix}/${id}`);

  references = async (id: AppId) => this.http.get<DefinitionReference[]>(`${this.prefix}/${id}/references`);
}
